
import { Task, Quadrant } from '../types';

export interface NudgeResult {
  taskId?: string;
  relevantTaskIds: string[];
  type: 'URGENT' | 'STRATEGIC' | 'DAILY_PULSE';
  message: string;
}

export const nudgeService = {
  /**
   * Determines if a specific task is stagnant (Idle in Q1 > 24h).
   */
  isTaskStagnant(task: Task): boolean {
    const dayMs = 24 * 60 * 60 * 1000;
    return (
      task.currentQuadrant === Quadrant.DO &&
      !task.isCompleted &&
      task.status !== 'ARCHIVED' &&
      (Date.now() - task.updatedAt) > dayMs
    );
  },

  /**
   * Scans tasks for stagnation in Q1 and Q2.
   * Returns a prioritized nudge if criteria are met.
   */
  detectActiveNudges(tasks: Task[]): NudgeResult | null {
    const incompleteTasks = tasks.filter(t => !t.isCompleted && t.status !== 'ARCHIVED');
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    // 1. Check for Q1 (Crisis) Stagnation
    // Use the specific helper logic for consistency
    const allStagnantQ1 = incompleteTasks
      .filter(t => this.isTaskStagnant(t))
      .sort((a, b) => a.updatedAt - b.updatedAt);

    if (allStagnantQ1.length > 0) {
      const primary = allStagnantQ1[0];
      return {
        taskId: primary.id,
        relevantTaskIds: allStagnantQ1.map(t => t.id),
        type: 'URGENT',
        message: `System Alert: Q1 Objective "${primary.title}" has been stagnant for over 24h. Secure this mission immediately to regain bandwidth.`
      };
    }

    // 2. Check for Q2 (Strategic Moat) Drift
    const recentlyCompletedQ2 = tasks.filter(t => 
      t.isCompleted && 
      t.currentQuadrant === Quadrant.SCHEDULE && 
      (now - (t.completedAt || 0)) < (dayMs * 2)
    );

    if (recentlyCompletedQ2.length === 0) {
      const allPendingQ2 = incompleteTasks
        .filter(t => t.currentQuadrant === Quadrant.SCHEDULE)
        .sort((a, b) => a.createdAt - b.createdAt);

      if (allPendingQ2.length > 0) {
        const primary = allPendingQ2[0];
        return {
          taskId: primary.id,
          relevantTaskIds: [primary.id],
          type: 'STRATEGIC',
          message: `The Strategic Moat is drifting. You are caught in the noise. Decompose "${primary.title}" to maintain high-impact momentum.`
        };
      }
    }

    return null;
  },

  /**
   * Generates a daily executive summary of priority objectives.
   */
  getDailyPulseSummary(tasks: Task[]): NudgeResult | null {
    const incompleteTasks = tasks.filter(t => !t.isCompleted && t.status !== 'ARCHIVED');
    const q1Count = incompleteTasks.filter(t => t.currentQuadrant === Quadrant.DO).length;
    const q2Count = incompleteTasks.filter(t => t.currentQuadrant === Quadrant.SCHEDULE).length;

    if (q1Count === 0 && q2Count === 0) return null;

    return {
      type: 'DAILY_PULSE',
      relevantTaskIds: [],
      message: `Operational Readiness: You have ${q1Count} Urgent crises and ${q2Count} Strategic objectives requiring deployment today. Eyes on the target.`
    };
  }
};
